import React, { FC, useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { TableHeaderCell } from './TableHeaderCell'
import { ColumnMenuPopover } from './ColumnMenuPopover'
import { ColumnChooserDrawer } from './ColumnChooserDrawer'
import { TableFooterPagination } from './TableFooterPagination'
import { EnhancedCell } from './components/EnhancedCell'
import { formatCellValue, getCellValue } from './formatters'
import Loader from 'src/utils/Loader'
import { resolveBundleStatus, resolveBundleStatusMessage } from './bundleStatusUtils'
import ProductLinkStatusTab from 'src/app/pages/listing/components/ProductLinkStatusTab'
import { getProductChannelLinkedStatus } from 'src/app/pages/listing/components/linkingStatusUtils'
import LinkButtonGroup from 'src/app/pages/listing/components/LinkButtonGroup'
import {
  CatalogColumn,
  SelectedColumn,
  SortState,
  FilterState,
  PaginationState,
  ProductVariant,
  DateRangeFilterValue,
  FilterValue,
} from './types'

interface ProductTableLiteProps {
  // Data
  data: ProductVariant[]
  loading: boolean

  // Columns
  catalogColumns: CatalogColumn[]
  selectedColumns: SelectedColumn[]
  onColumnsChange: (columns: SelectedColumn[]) => void
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void
  onPinColumn?: (field: string, position: 'left' | 'right' | null) => void
  getColumnByField: (field: string) => CatalogColumn | undefined

  // Sorting
  sortState: SortState | null
  onSortChange: (field: string, direction: 'asc' | 'desc') => void

  // Filtering
  filterState: FilterState
  onFilterChange: (filters: FilterState) => void

  // Pagination
  paginationState: PaginationState
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void

  // Actions
  onRefresh?: () => void
  onExportCSV?: () => void
  onResetColumns?: () => void

  // Platform context for special columns
  platformId?: string
  channelId?: string
  mainStore?: boolean
  erpStore?: boolean
  tabsData?: any[]
  onLinkClick?: (rowData: any) => void
  onResyncClick?: (rowData: any) => void
  onResyncSelected?: () => void
  resyncSelectedCount?: number
  resyncSelectedLoading?: boolean
  enableBulkActions?: boolean
  selectedAction?: string
  onSelectedActionChange?: (action: string) => void
  bulkActionOptions?: Array<{ value: string; label: string; requiresSelection?: boolean }>
  selectedCount?: number
  selectionEnabled?: boolean
  selectedRowIds?: Array<number | string>
  onSelectedRowIdsChange?: (ids: Array<number | string>) => void
  isRowSelectable?: (row: ProductVariant) => boolean
  mandatoryColumns?: string[]
  onOpenColumnsConfig?: () => Promise<void> | void
  defaultVisibleFields?: string[]
}

export const ProductTableLite: FC<ProductTableLiteProps> = ({
  data,
  loading,
  catalogColumns,
  selectedColumns,
  onColumnsChange,
  onReorderColumns,
  onPinColumn,
  getColumnByField,
  sortState,
  onSortChange,
  filterState,
  onFilterChange,
  paginationState,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onExportCSV,
  onResetColumns,
  platformId,
  channelId,
  mainStore,
  erpStore,
  tabsData = [],
  onLinkClick,
  onResyncClick,
  onResyncSelected,
  resyncSelectedCount = 0,
  resyncSelectedLoading = false,
  enableBulkActions = false,
  selectedAction = '',
  onSelectedActionChange,
  bulkActionOptions = [
    { value: 'delist', label: 'Delist', requiresSelection: true },
    { value: 'relist', label: 'Relist', requiresSelection: true },
    { value: 'full_update', label: 'Full Update' },
  ],
  selectedCount = 0,
  selectionEnabled = false,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  isRowSelectable,
  mandatoryColumns = ['product_name', 'linking_status', 'link_actions'],
  onOpenColumnsConfig,
  defaultVisibleFields = [],
}) => {
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)
  const [activeColumn, setActiveColumn] = useState<CatalogColumn | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [columnChooserOpen, setColumnChooserOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(filterState.globalSearch)
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const selectedIdSet = new Set((selectedRowIds || []).map(String))
  const mandatorySet = new Set((mandatoryColumns || []).filter(Boolean))

  const isDateRangeFilterValue = (value: FilterValue | ''): value is DateRangeFilterValue =>
    typeof value === 'object' && value !== null && ('from' in value || 'to' in value)

  const hasFilterValue = (value: FilterValue | '') => {
    if (isDateRangeFilterValue(value)) return Boolean(value.from || value.to)
    return value !== '' && value !== null && value !== undefined
  }

  useEffect(() => {
    setSearchQuery(filterState.globalSearch || '')
  }, [filterState.globalSearch])

  useEffect(() => {
    if (!tableScrollRef.current) return
    tableScrollRef.current.scrollLeft = 0
    tableScrollRef.current.scrollTop = 0
  }, [platformId, channelId])

  // Handle column menu open
  const handleOpenColumnMenu = useCallback(
    (column: CatalogColumn, anchorEl: HTMLElement) => {
      const sameColumn = activeColumn?.field === column.field
      const sameAnchor = menuAnchorEl === anchorEl

      if (columnMenuOpen && sameColumn && sameAnchor) {
        setColumnMenuOpen(false)
        setActiveColumn(null)
        setMenuAnchorEl(null)
        return
      }

      setActiveColumn(column)
      setMenuAnchorEl(anchorEl)
      setColumnMenuOpen(true)
    },
    [activeColumn, menuAnchorEl, columnMenuOpen]
  )

  // Handle column menu close
  const handleCloseColumnMenu = useCallback(() => {
    setColumnMenuOpen(false)
    setActiveColumn(null)
    setMenuAnchorEl(null)
  }, [])

  // Handle sort
  const handleSort = useCallback(
    (direction: 'asc' | 'desc') => {
      if (activeColumn) {
        onSortChange(activeColumn.field, direction)
      }
    },
    [activeColumn, onSortChange]
  )

  // Handle column filter
  const handleColumnFilter = useCallback(
    (value: FilterValue | '', operator: string = 'contains') => {
      if (!activeColumn) return

      const existingFilter = filterState.columnFilters.find(
        (f) => f.field === activeColumn.field
      )
      if (!hasFilterValue(value) && !existingFilter) {
        return
      }

      const newFilters = filterState.columnFilters.filter(
        (f) => f.field !== activeColumn.field
      )

      if (hasFilterValue(value)) {
        newFilters.push({
          field: activeColumn.field,
          operator,
          value,
        })
      }

      onFilterChange({
        ...filterState,
        columnFilters: newFilters,
      })
    },
    [activeColumn, filterState, onFilterChange]
  )

  // Handle global search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value)

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        onFilterChange({
          ...filterState,
          globalSearch: value,
        })
      }, 500)
    },
    [filterState, onFilterChange]
  )

  // Handle autosize (placeholder)
  const handleAutosize = useCallback(() => {
    console.log('Autosize column:', activeColumn?.field)
    // TODO: Implement column width calculation
  }, [activeColumn])

  const handleAutosizeAll = useCallback(() => {
    console.log('Autosize all columns')
    // TODO: Implement all columns width calculation
  }, [])

  // Toggle column visibility
  const handleToggleColumn = useCallback(
    (field: string) => {
      const catalogColumn = getColumnByField(field)
      if (mandatorySet.has(field) || catalogColumn?.fixed || catalogColumn?.allowVisibility === 'ALWAYS_VISIBLE') return
      const exists = selectedColumns.find((col) => col.field === field)

      if (exists) {
        // Remove
        const updated = selectedColumns
          .filter((col) => col.field !== field)
          .map((col, idx) => ({ ...col, order: idx }))
        onColumnsChange(updated)
      } else {
        // Add
        const maxOrder = selectedColumns.length > 0
          ? Math.max(...selectedColumns.map((c) => c.order))
          : -1
        onColumnsChange([...selectedColumns, { field, order: maxOrder + 1 }])
      }
    },
    [selectedColumns, onColumnsChange, mandatorySet, getColumnByField]
  )

  // Toggle multiple columns at once (for Select All)
  const handleToggleMultipleColumns = useCallback(
    (fields: string[]) => {
      const allowedFields = fields.filter((field) => {
        if (mandatorySet.has(field)) return false
        const col = getColumnByField(field)
        return !(col?.fixed || col?.allowVisibility === 'ALWAYS_VISIBLE')
      })
      if (allowedFields.length === 0) return
      const existingFields = new Set(selectedColumns.map((col) => col.field))
      const missingFields = allowedFields.filter((field) => !existingFields.has(field))
      if (missingFields.length === 0) return
      
      let maxOrder = selectedColumns.length > 0
        ? Math.max(...selectedColumns.map((c) => c.order))
        : -1
      
      const newColumns = missingFields.map((field) => {
        maxOrder += 1
        return { field, order: maxOrder }
      })
      
      onColumnsChange([...selectedColumns, ...newColumns])
    },
    [selectedColumns, onColumnsChange, mandatorySet, getColumnByField]
  )

  const handleUncheckAllColumns = useCallback(() => {
    const defaultSet = new Set((defaultVisibleFields || []).filter(Boolean))
    const kept = selectedColumns.filter((col) => {
      const catalogColumn = getColumnByField(col.field)
      return (
        defaultSet.has(col.field) ||
        mandatorySet.has(col.field) ||
        Boolean(catalogColumn?.fixed) ||
        catalogColumn?.allowVisibility === 'ALWAYS_VISIBLE'
      )
    })
    const normalized = kept
      .sort((a, b) => a.order - b.order)
      .map((col, idx) => ({ ...col, order: idx }))
    onColumnsChange(normalized)
  }, [selectedColumns, getColumnByField, mandatorySet, onColumnsChange, defaultVisibleFields])

  // Get current filter for active column
  const getCurrentFilter = () => {
    if (!activeColumn) return ''
    const filter = filterState.columnFilters.find(
      (f) => f.field === activeColumn.field
    )
    return filter?.value ?? ''
  }

  const getCurrentOperator = () => {
    if (!activeColumn) return 'contains'
    const filter = filterState.columnFilters.find(
      (f) => f.field === activeColumn.field
    )
    return filter?.operator || activeColumn.filterOperators?.[0] || 'contains'
  }

  const formatHeaderToken = (value: string) => {
    const token = value.trim()
    const lower = token.toLowerCase()

    if (lower === 'sku') return 'SKU'
    if (lower === 'id') return 'ID'
    if (lower === 'api') return 'API'
    if (lower === 'erp') return 'ERP'

    return lower.charAt(0).toUpperCase() + lower.slice(1)
  }

  const formatHeaderLabel = (column: CatalogColumn) => {
    const field = column.field || ''
    const apiHeaderName = (column.headerName || '').trim()
    if (apiHeaderName) return apiHeaderName
    if (field === 'product_name' || field === 'name') return 'Product Name'
    if (field === 'linking_status') return 'Linking Status'
    if (field === 'link_actions') return 'Link Actions'
    if (field === 'sku') return 'Channel SKU'
    if (field === 'api_id') return 'SKU Id'
    if (field === 'compare_price') return 'Wholesale Price'
    if (field === 'api_code') return 'Feed Id'
    if (field === 'quantity') return 'Inventory Level'
    if (field === 'api_status') return 'Status'

    if (field.endsWith('_sync_status')) {
      if (field === 'sync_status') return 'Source Sync Status'
      const type = field.replace(/_sync_status$/, '')
      return `${type.split('_').map(formatHeaderToken).join(' ')} Sync Status`
    }

    const rawLabel = (column.label || column.name || field || '').trim()
    if (!rawLabel) return field

    return rawLabel
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(formatHeaderToken)
      .join(' ')
  }

  const isMenuDisabledForColumn = (column: CatalogColumn) => {
    if (!column?.field) return false
    return column.sortable === false && column.resizable === false && column.filterable === false
  }

  const hasActiveFilterForField = (field: string) =>
    filterState.columnFilters.some((f) => f.field === field && hasFilterValue(f.value))

  const shouldCenterAlign = (field: string) => {
    if (!field) return false
    return (
      field === 'linking_status' ||
      field === 'link_actions' ||
      field === 'sku' ||
      field === 'quantity' ||
      field === 'api_status' ||
      field.endsWith('_sync_status') ||
      field === 'sync_status'
    )
  }

  const shouldWrapCell = (field: string) =>
    field === 'product_name' ||
    field === 'name' ||
    field === 'description'

  const getRowLinkingStatus = (row: ProductVariant): 'linked' | 'unlinked' => {
    if (!Array.isArray(tabsData) || tabsData.length === 0) {
      return 'unlinked'
    }
    const anyLinked = tabsData.some((item: any) => {
      return getProductChannelLinkedStatus(row, item?.type, item?.id) === 'linked'
    })
    return anyLinked ? 'linked' : 'unlinked'
  }

  const resolveLinkingStatusFilter = (value: FilterValue): 'linked' | 'unlinked' | null => {
    if (value === true || value === 'true') return 'linked'
    if (value === false || value === 'false') return 'unlinked'
    const normalized = String(value).toLowerCase().trim()
    if (normalized === 'linked') return 'linked'
    if (normalized === 'unlinked') return 'unlinked'
    return null
  }

  const renderedRows = React.useMemo(() => data, [data])

  const selectableRows = React.useMemo(
    () => renderedRows.filter((row) => (isRowSelectable ? isRowSelectable(row) : true)),
    [renderedRows, isRowSelectable]
  )

  const isRowSelected = (row: ProductVariant) => selectedIdSet.has(String(row.id))

  const toggleRowSelection = (row: ProductVariant) => {
    if (!onSelectedRowIdsChange) return
    if (isRowSelectable && !isRowSelectable(row)) return
    const id = row.id
    const exists = selectedIdSet.has(String(id))
    if (exists) {
      onSelectedRowIdsChange((selectedRowIds || []).filter((x) => String(x) !== String(id)))
    } else {
      onSelectedRowIdsChange([...(selectedRowIds || []), id])
    }
  }

  const allVisibleSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedIdSet.has(String(row.id)))
  const toggleSelectAllVisible = () => {
    if (!onSelectedRowIdsChange) return
    if (allVisibleSelected) {
      const visibleIds = new Set(selectableRows.map((row) => String(row.id)))
      onSelectedRowIdsChange((selectedRowIds || []).filter((id) => !visibleIds.has(String(id))))
    } else {
      const merged = new Set((selectedRowIds || []).map(String))
      selectableRows.forEach((row) => merged.add(String(row.id)))
      onSelectedRowIdsChange(Array.from(merged))
    }
  }

  // Render cell value
  const renderCellValue = (row: ProductVariant, column: CatalogColumn, rowIndex: number) => {
    const getLinkedPlatformNames = () => {
      if (!Array.isArray(tabsData) || tabsData.length === 0) return []
      return tabsData
        .filter((item: any) => {
          return getProductChannelLinkedStatus(row, item?.type, item?.id) === 'linked'
        })
        .map((item: any) => item?.name || item?.type)
        .filter(Boolean)
    }

    // Handle Product Name column
    if (column.field === 'product_name' || column.field === 'name') {
      const platform = row?.platform_product?.platform
      const productId = row?.platform_product?.id
      const variantId = row?.id
      const apiName = row?.name || ''
      const productName = row?.platform_product?.name || row?.product_name || ''
      const normalizedApiName = String(apiName).replace(/\s+/g, ' ').trim()
      const normalizedProductName = String(productName).replace(/\s+/g, ' ').trim()
      // row.name carries the spacing the API returns; the nested product copy may not.
      // Prefer row.name whenever both hold the same title.
      const name =
        apiName &&
        apiName !== 'Default Title' &&
        (!productName || normalizedApiName === normalizedProductName)
          ? apiName
          : productName || apiName
      const variantName = row?.name
      const variantSuffix =
        variantName && variantName !== 'Default Title' && variantName !== name ? `Variant : ${variantName}` : ''

      return (
        <div className='d-flex flex-column'>
          <Link
            className='fw-bold text-info mb-0 text-decoration-none'
            to={`/product-variant-detail/${variantId}/platform/${platform}/product/${productId}/${platformId}`}
          >
            <span className='text-preserve-spacing'>{name}</span>
          </Link>
          {variantSuffix && <small className='fw-bold text-preserve-spacing'>{variantSuffix}</small>}
        </div>
      )
    }

    // Handle Linking Status column
    if (column.field === 'linking_status') {
      return (
        <span className='d-inline-flex'>
          <ProductLinkStatusTab data={row} channels={tabsData} />
        </span>
      )
    }

    // Handle Link Actions column
    if (column.field === 'link_actions') {
      return (
        <LinkButtonGroup
          rowData={row}
          rowIndex={rowIndex}
          onClick={() => onLinkClick?.(row)}
          onCloseTooltip={() => {}}
          platformId={platformId}
          mainStore={mainStore}
          erpStore={erpStore}
          channels={tabsData}
        />
      )
    }

    // Old grid renders SKU as plain text (not link).
    if (column.field === 'sku') {
      const sku = getCellValue(row, column.field)
      if (!sku) return <span className="text-muted">-</span>
      return <span>{sku}</span>
    }

    const value = getCellValue(row, column.field)
    
    // Determine cell type based on column field
    let cellType: 'text' | 'status' | 'productName' | 'price' | 'badge' | 'date' = 'text'
    
    if (column.field === 'price' || column.field === 'compare_price' || column.field === 'wholesale_price') {
      cellType = 'price'
    } else if (column.field.includes('sync_status')) {
      cellType = 'status'
    } else if (column.field.includes('status') || column.field === 'api_status') {
      cellType = 'badge'
    } else if (column.field.includes('_at') || column.type === 'date') {
      cellType = 'date'
      // Format date if needed
      return <EnhancedCell value={formatCellValue(value, 'date', column.format)} type={cellType} row={row} />
    }
    
    // For price fields, we need the raw price object for EnhancedCell to extract currency
    if (cellType === 'price') {
      const rawPrice =
        column.field === 'compare_price' || column.field === 'wholesale_price'
          ? row[column.field] ||
            row?.platform_product?.[column.field] ||
            getCellValue(row, column.field)
          : row.price || row?.platform_product?.price || getCellValue(row, column.field)
      return <EnhancedCell value={rawPrice} type={cellType} row={row} />
    }

    if (cellType === 'status') {
      const syncMessageField =
        column.field === 'sync_status' ? 'sync_message' : column.field.replace(/_status$/, '_message')
      const resolvedStatus = resolveBundleStatus(row, column.field, value)
      const syncMessage = resolveBundleStatusMessage(row, column.field, row?.[syncMessageField])
      const isSyncStatusField = column.field === 'sync_status' || column.field.endsWith('_sync_status')
      const shouldShowResync =
        isSyncStatusField && ['PROCESSING', 'ERROR', 'ERROR_UNLINKED', 'ERROR_NO_MAPPING'].includes(String(resolvedStatus || ''))

      return (
        <div className='d-flex align-items-center justify-content-center gap-2'>
          <EnhancedCell value={resolvedStatus} type={cellType} row={row} syncMessage={syncMessage} />
          {shouldShowResync && (
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id={`resync-${row?.id}`}>Click to resync the product variant</Tooltip>}
            >
              <Button
                variant='link'
                className='p-0 m-0'
                onClick={() => onResyncClick?.(row)}
              >
                <i className='bi bi-arrow-repeat text-danger' />
              </Button>
            </OverlayTrigger>
          )}
        </div>
      )
    }
    
    return <EnhancedCell value={value} type={cellType} row={row} />
  }

  // Organize columns by pinning
  const organizedColumns = React.useMemo(() => {
    const sorted = [...selectedColumns].sort((a, b) => a.order - b.order)
    
    const pinnedLeft: SelectedColumn[] = []
    const unpinned: SelectedColumn[] = []
    const pinnedRight: SelectedColumn[] = []
    
    sorted.forEach(col => {
      if (col.pinned === 'left') {
        pinnedLeft.push(col)
      } else if (col.pinned === 'right') {
        pinnedRight.push(col)
      } else {
        unpinned.push(col)
      }
    })
    
    return { pinnedLeft, unpinned, pinnedRight, all: [...pinnedLeft, ...unpinned, ...pinnedRight] }
  }, [selectedColumns])

  // Calculate sticky positions
  const getStickyStyle = (column: SelectedColumn, index: number, isHeader: boolean = false): React.CSSProperties => {
    if (!column.pinned) return {}
    
    const baseZIndex = isHeader ? 20 : 10
    
    if (column.pinned === 'left') {
      // Calculate offset from all previous left-pinned columns
      let leftOffset = 0
      for (let i = 0; i < index; i++) {
        const prevCol = organizedColumns.pinnedLeft[i]
        if (prevCol) {
          const catalogCol = getColumnByField(prevCol.field)
          leftOffset += prevCol.width || catalogCol?.width || 150
        }
      }
      
      return {
        position: 'sticky' as const,
        left: leftOffset,
        zIndex: baseZIndex,
        backgroundColor: '#fff',
        borderRight: index === organizedColumns.pinnedLeft.length - 1 ? '2px solid #e4e6ef' : undefined,
      }
    } else if (column.pinned === 'right') {
      // Calculate offset from all following right-pinned columns
      let rightOffset = 0
      const rightIndex = organizedColumns.pinnedRight.findIndex(c => c.field === column.field)
      for (let i = rightIndex + 1; i < organizedColumns.pinnedRight.length; i++) {
        const nextCol = organizedColumns.pinnedRight[i]
        if (nextCol) {
          const catalogCol = getColumnByField(nextCol.field)
          rightOffset += nextCol.width || catalogCol?.width || 150
        }
      }
      
      return {
        position: 'sticky' as const,
        right: rightOffset,
        zIndex: baseZIndex,
        backgroundColor: '#fff',
        borderLeft: rightIndex === 0 ? '2px solid #e4e6ef' : undefined,
      }
    }
    
    return {}
  }

  return (
    <>
      <style>
        {`
          .flat-search-input {
            border: 1px solid #d1d3e0;
            border-radius: 6px;
            padding: 0.5rem 0.75rem 0.5rem 2.35rem;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            background: #ffffff;
          }
          .flat-search-input:focus {
            border-color: #3699ff;
            box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.1);
            outline: none;
          }
          .flat-btn {
            border-radius: 6px;
            padding: 0.45rem 0.9rem;
            font-weight: 600;
            font-size: 0.82rem;
            transition: all 0.2s ease;
            border: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
          }
          .flat-btn-primary {
            background: #3699ff;
            color: #ffffff;
          }
          .flat-btn-primary:hover {
            background: #187de4;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(54, 153, 255, 0.3);
          }
          .flat-btn-secondary {
            background: #f3f6f9;
            color: #7e8299;
          }
          .flat-btn-secondary:hover {
            background: #e4e6ef;
            color: #3f4254;
            transform: translateY(-1px);
          }
          .flat-btn-icon {
            border-radius: 8px;
            padding: 0.65rem 0.75rem;
            background: #f3f6f9;
            color: #7e8299;
            border: none;
            transition: all 0.2s ease;
          }
          .flat-btn-icon:hover {
            background: #e4e6ef;
            color: #3f4254;
            transform: translateY(-1px);
          }
          .flat-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none !important;
          }
          .listing-lite-table .table.table-row-bordered > thead > tr > th,
          .listing-lite-table .table.table-row-bordered > tbody > tr > td {
            border-bottom-color: rgba(0, 0, 0, 0.12) !important;
          }
          .listing-lite-table .table-scroll-container {
            max-height: 62vh;
            overflow: auto;
            position: relative;
          }
          .listing-lite-table .table-scroll-container thead th {
            position: sticky;
            top: 0;
            z-index: 12;
            background: #fff;
          }
        `}
      </style>
      <div className="card listing-lite-table">
        {/* Toolbar */}
        <div className="card-header border-0 py-3 px-4" >
          <div className="card-title">
            {/* Search */}
            <div className="d-flex align-items-center position-relative">
              <i className="bi bi-search position-absolute ms-3 text-muted" style={{ fontSize: '1rem' }}></i>
              <input
                type="text"
                className="flat-search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{ width: '200px' }}
              />
            </div>
          </div>

          <div className="card-toolbar">
            <div className="d-flex flex-md-row flex-column justify-content-md-end align-items-md-center gap-2">
              {enableBulkActions && (
                <select
                  className='form-select me-md-1'
                  aria-label='Select Action'
                  value={selectedAction}
                  onChange={(ev) => onSelectedActionChange?.(ev.target.value)}
                  style={{ minWidth: '145px', fontSize: '0.85rem' }}
                >
                  <option value={''}>Action</option>
                  {bulkActionOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={Boolean(option.requiresSelection && !selectedCount)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              )}

              {/* Column chooser button */}
              <button
                className="flat-btn flat-btn-secondary"
                onClick={async () => {
                  if (onOpenColumnsConfig) {
                    await onOpenColumnsConfig()
                  }
                  setColumnChooserOpen(true)
                }}
              >
                <i className="bi bi-columns-gap"></i>
                Columns
              </button>

              {/* Export CSV button */}
              {onExportCSV && (
                <button
                  className="flat-btn flat-btn-secondary"
                  onClick={onExportCSV}
                  disabled={loading}
                >
                  <i className="bi bi-download"></i>
                  Export CSV
                </button>
              )}

              {/* Reset columns button */}
              {onResetColumns && (
                <button
                  className="flat-btn flat-btn-secondary"
                  onClick={onResetColumns}
                  disabled={loading}
                  title="Reset to default columns"
                >
                  <i className="bi bi-arrow-repeat"></i>
                  Reset
                </button>
              )}

              {onResyncSelected && resyncSelectedCount > 0 && (
                <button
                  className="flat-btn flat-btn-primary"
                  onClick={onResyncSelected}
                  disabled={loading || resyncSelectedLoading}
                  title="Resync selected failed products"
                >
                  <i className="bi bi-upload text-white"></i>
                  {resyncSelectedLoading ? 'Resyncing...' : `Resync Selected (${resyncSelectedCount})`}
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Table */}
      <div className="card-body p-0">
        <div
          ref={tableScrollRef}
          className="table-responsive table-scroll-container"
          style={{ overflowX: 'auto', position: 'relative' }}
        >
          <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3" style={{ position: 'relative' }}>
            <thead>
              <tr className="fw-bold text-muted">
                {selectionEnabled && (
                  <th
                    className='text-center'
                    style={{ minWidth: 54, width: 54, padding: '12px 8px', verticalAlign: 'middle' }}
                  >
                    <input
                      type='checkbox'
                      checked={allVisibleSelected}
                      disabled={selectableRows.length === 0}
                      onChange={toggleSelectAllVisible}
                    />
                  </th>
                )}
                {organizedColumns.all.map((selectedCol, idx) => {
                    const column = getColumnByField(selectedCol.field)
                    if (!column) return null

                    const pinIndex = selectedCol.pinned === 'left'
                        ? organizedColumns.pinnedLeft.findIndex(c => c.field === selectedCol.field)
                        : selectedCol.pinned === 'right'
                        ? organizedColumns.pinnedRight.findIndex(c => c.field === selectedCol.field)
                        : -1

                    const stickyStyle = getStickyStyle(selectedCol, pinIndex, true)

                    return (
                      <th 
                        key={column.field} 
                        className={shouldCenterAlign(column.field) ? 'text-center' : 'text-start'}
                        style={{
                          ...stickyStyle,
                          minWidth: selectedCol.width || column.width || 150,
                          padding: '12px 16px',
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div className='d-flex flex-column gap-2'>
                          <TableHeaderCell
                            column={column}
                            displayLabel={formatHeaderLabel(column)}
                            sortState={sortState}
                            onOpenMenu={handleOpenColumnMenu}
                            menuDisabled={isMenuDisabledForColumn(column)}
                            hasActiveFilter={hasActiveFilterForField(column.field)}
                          />
                        </div>
                      </th>
                    )
                  })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={selectedColumns.length + (selectionEnabled ? 1 : 0)} className="text-center py-10">
                    <Loader variant="table" />
                  </td>
                </tr>
              ) : renderedRows.length === 0 ? (
                <tr>
                  <td colSpan={selectedColumns.length + (selectionEnabled ? 1 : 0)} className="text-center py-10 text-muted">
                    No data available
                  </td>
                </tr>
              ) : (
                renderedRows.map((row, rowIndex) => (
                  <tr key={row.id || rowIndex}>
                    {selectionEnabled && (
                      <td className='text-center' style={{ minWidth: 54, width: 54, padding: '12px 8px' }}>
                        <input
                          type='checkbox'
                          checked={isRowSelected(row)}
                          disabled={Boolean(isRowSelectable && !isRowSelectable(row))}
                          onChange={() => toggleRowSelection(row)}
                        />
                      </td>
                    )}
                    {organizedColumns.all.map((selectedCol, idx) => {
                        const column = getColumnByField(selectedCol.field)
                        if (!column) return null

                        const pinIndex = selectedCol.pinned === 'left'
                            ? organizedColumns.pinnedLeft.findIndex(c => c.field === selectedCol.field)
                            : selectedCol.pinned === 'right'
                            ? organizedColumns.pinnedRight.findIndex(c => c.field === selectedCol.field)
                            : -1

                        const stickyStyle = getStickyStyle(selectedCol, pinIndex, false)

                        return (
                          <td
                            key={column.field}
                            className={`text-dark fw-normal ${shouldCenterAlign(column.field) ? 'text-center' : ''}`}
                            style={{
                              whiteSpace: shouldWrapCell(column.field) ? 'normal' : 'nowrap',
                              wordBreak: shouldWrapCell(column.field) ? 'break-word' : 'normal',
                              maxWidth: shouldWrapCell(column.field) ? 420 : 240,
                              overflow: 'hidden',
                              textOverflow: shouldWrapCell(column.field) ? 'clip' : 'ellipsis',
                              minWidth: selectedCol.width || column.width || 150,
                              padding: '12px 16px',
                              verticalAlign: 'middle',
                              ...stickyStyle
                            }}
                          >
                            {renderCellValue(row, column, rowIndex)}
                          </td>
                        )
                      })} 
                  </tr>
                )) 
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer pagination */} 
      <TableFooterPagination
        paginationState={paginationState}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        loading={loading}
      />

      {/* Column menu popover */} 
      {activeColumn && (
        <ColumnMenuPopover
          column={activeColumn}
          isOpen={columnMenuOpen}
          onClose={handleCloseColumnMenu}
          onSortAsc={() => handleSort('asc')}
          onSortDesc={() => handleSort('desc')}
          onAutosize={handleAutosize}
          onAutosizeAll={handleAutosizeAll}
          onFilterChange={handleColumnFilter}
          currentFilter={getCurrentFilter()}
          currentOperator={getCurrentOperator()}
          anchorEl={menuAnchorEl}
        />
      )} 

      {/* Column chooser drawer */}
      <ColumnChooserDrawer
        isOpen={columnChooserOpen}
        onClose={() => setColumnChooserOpen(false)}
        catalogColumns={catalogColumns}
        selectedColumns={selectedColumns}
        onToggleColumn={handleToggleColumn}
        onToggleMultipleColumns={handleToggleMultipleColumns}
        onUncheckAllColumns={handleUncheckAllColumns}
        onReorderColumns={onReorderColumns}
        onPinColumn={onPinColumn}
        getColumnByField={getColumnByField}
        mandatoryColumns={mandatoryColumns}
      />
    </div>
    </> 
  ) 
} 
